import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ShoppingCart, ExternalLink, Download, Check, Code,
  Star, Shield, RefreshCcw, Zap, Lock, CheckCircle, Share2, Facebook, Twitter, Linkedin
} from 'lucide-react';
import { getCurrentUser } from '@/utils/config';
import SEO from '@/components/common/SEO';
import { useToast } from '@/contexts/ToastContext';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const toast = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('paystack'); // 'paystack' or 'crypto'
  const [cryptoAddresses, setCryptoAddresses] = useState(null);
  const [selectedCrypto, setSelectedCrypto] = useState('bitcoin');
  const [cryptoTxId, setCryptoTxId] = useState('');
  const [submittingCrypto, setSubmittingCrypto] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await fetch(`/.netlify/functions/product?slug=${slug}`);
        const data = await response.json();

        if (data.success) {
          setProduct(data.data);

          // Check if user has access to this product
          const token = localStorage.getItem('token');
          if (token) {
            checkUserAccess(data.data.id);
          } else {
            setHasAccess(false);
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  // Fetch crypto addresses when crypto payment is selected
  useEffect(() => {
    if (paymentMethod === 'crypto' && !cryptoAddresses) {
      fetchCryptoAddresses();
    }
  }, [paymentMethod]);

  const fetchCryptoAddresses = async () => {
    try {
      const response = await fetch('/.netlify/functions/crypto-settings');
      const data = await response.json();
      if (data.success) {
        setCryptoAddresses(data.data);
      }
    } catch (error) {
      console.error('Error fetching crypto addresses:', error);
    }
  };

  const checkUserAccess = async (productId) => {
    try {
      setCheckingAccess(true);
      const token = localStorage.getItem('token');

      // Don't make the request if there's no token
      if (!token) {
        setHasAccess(false);
        return;
      }

      const response = await fetch('/.netlify/functions/premium-access', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 as expected (user not authenticated or token expired)
      if (response.status === 401) {
        setHasAccess(false);
        return;
      }

      const data = await response.json();

      if (data.success) {
        const hasProduct = data.data.products?.some(item => item.product.id === productId);
        setHasAccess(hasProduct);
      } else {
        setHasAccess(false);
      }
    } catch (err) {
      console.error('Error checking access:', err);
      setHasAccess(false);
    } finally {
      setCheckingAccess(false);
    }
  };

  const handlePurchase = async () => {
    // Check if user is logged in
    if (!user) {
      toast.warning('Please login or create an account to purchase');
      navigate('/login', { state: { from: `/shop/${slug}` } });
      return;
    }

    try {
      setPurchasing(true);
      const token = localStorage.getItem('token');

      const response = await fetch('/.netlify/functions/order-create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: [{
            productId: product.id,
            quantity: 1
          }]
        })
      });

      const data = await response.json();

      if (response.status === 401) {
        toast.error('Your session has expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login', { state: { from: `/shop/${slug}` } });
        return;
      }

      if (data.success) {
        // Redirect to Paystack payment page
        window.location.href = data.data.payment.authorization_url;
      } else {
        toast.error(data.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Failed to process purchase. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCryptoPayment = async () => {
    if (!cryptoTxId || cryptoTxId.trim().length === 0) {
      toast.warning('Please enter your crypto transaction ID/reference');
      return;
    }

    try {
      setSubmittingCrypto(true);
      const token = localStorage.getItem('token');

      const response = await fetch('/.netlify/functions/order-create-crypto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: [{
            productId: product.id,
            quantity: 1
          }],
          cryptoType: selectedCrypto,
          cryptoTransactionId: cryptoTxId
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(data.data.message || 'Your crypto payment has been submitted for review. You will receive an email once approved.');
        navigate('/dashboard');
      } else {
        toast.error(data.message || 'Failed to submit crypto payment');
      }
    } catch (error) {
      console.error('Crypto payment error:', error);
      toast.error('Failed to process crypto payment. Please try again.');
    } finally {
      setSubmittingCrypto(false);
    }
  };

  const formatPrice = (price, currency = 'GHS') => {
    if (currency === 'GHS') {
      return `GH₵${price.toLocaleString()}`;
    }
    return `$${price.toLocaleString()}`;
  };

  const shareProduct = (platform) => {
    const url = window.location.href;
    const text = `Check out ${product.name} - ${product.short_description}`;

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white pt-20">
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-700 rounded w-1/2"></div>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="h-96 bg-gray-700 rounded"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6"></div>
                <div className="h-4 bg-gray-700 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white pt-20">
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold mb-4">Product Not Found</h1>
          <p className="text-gray-400 mb-8">{error}</p>
          <button
            onClick={() => navigate('/shop')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white pt-20">
      {/* Dynamic SEO Meta Tags */}
      <SEO
        title={product.name}
        description={product.short_description || product.description?.substring(0, 160).replace(/<[^>]*>/g, '')}
        image={product.image_url}
        url={`/shop/${product.slug}`}
        type="website"
      />

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/shop')}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-emerald-400 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Shop
        </button>
      </div>

      {/* Product Details */}
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left Column - Image & Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Product Image */}
            {product.image_url ? (
              <div className="rounded-lg overflow-hidden shadow-2xl mb-8">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-auto"
                />
              </div>
            ) : (
              <div className="rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 aspect-video flex items-center justify-center shadow-2xl mb-8">
                <Code size={120} className="text-white opacity-50" />
              </div>
            )}

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Key Features</h3>
                <ul className="space-y-3">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check size={20} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>

          {/* Right Column - Purchase */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {/* Category & Downloads */}
            <div className="flex items-center gap-4 mb-4">
              {product.category && (
                <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-semibold">
                  {product.category}
                </span>
              )}
              <span className="flex items-center gap-2 text-gray-400">
                <Download size={16} />
                {product.downloads} downloads
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-bold mb-4">{product.name}</h1>

            {/* Short Description */}
            <p className="text-xl text-gray-300 mb-6">
              {product.short_description}
            </p>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-6">
              <span className="text-gray-400 text-sm">Share:</span>
              <button
                onClick={() => shareProduct('facebook')}
                className="p-2 bg-gray-800 hover:bg-blue-600 rounded-lg transition-colors"
                aria-label="Share on Facebook"
              >
                <Facebook size={18} />
              </button>
              <button
                onClick={() => shareProduct('twitter')}
                className="p-2 bg-gray-800 hover:bg-sky-500 rounded-lg transition-colors"
                aria-label="Share on Twitter"
              >
                <Twitter size={18} />
              </button>
              <button
                onClick={() => shareProduct('linkedin')}
                className="p-2 bg-gray-800 hover:bg-blue-700 rounded-lg transition-colors"
                aria-label="Share on LinkedIn"
              >
                <Linkedin size={18} />
              </button>
              <button
                onClick={copyLink}
                className="p-2 bg-gray-800 hover:bg-emerald-600 rounded-lg transition-colors"
                aria-label="Copy link"
              >
                <Share2 size={18} />
              </button>
            </div>

            {/* Price */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <div className="text-sm text-gray-400 mb-2">One-time payment</div>
              <div className="text-4xl font-bold text-emerald-400 mb-4">
                {formatPrice(parseFloat(product.price), product.currency)}
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                {product.version && <span>Version {product.version}</span>}
                {product.file_size && (
                  <>
                    <span>•</span>
                    <span>{product.file_size}</span>
                  </>
                )}
              </div>
            </div>

            {/* Purchase Section */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              {hasAccess ? (
                /* User Already Owns This */
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">You Own This Product</h3>
                  <p className="text-gray-400 mb-6">
                    Access your purchase from your dashboard
                  </p>
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold transition-colors"
                  >
                    <Download size={20} />
                    Go to Dashboard
                  </Link>
                </div>
              ) : !user ? (
                /* User Not Logged In */
                <div className="text-center py-4">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Lock className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Login Required</h3>
                  <p className="text-gray-400 mb-6">
                    Create an account or login to purchase this product
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/login"
                      state={{ from: `/shop/${slug}` }}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-lg font-semibold transition-colors"
                    >
                      Login to Purchase
                    </Link>
                    <Link
                      to="/register"
                      state={{ from: `/shop/${slug}` }}
                      className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors"
                    >
                      Create Account
                    </Link>
                  </div>
                </div>
              ) : (
                /* User Logged In - Can Purchase */
                <div>
                  <h3 className="text-lg font-semibold mb-4">Ready to Purchase</h3>
                  <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-gray-400">Account:</span>
                      <span className="text-white">{user.email}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Name:</span>
                      <span className="text-white">{user.full_name}</span>
                    </div>
                  </div>

                  {/* Payment Method Selection */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-3">Select Payment Method</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setPaymentMethod('paystack')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          paymentMethod === 'paystack'
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                        }`}
                      >
                        <div className="text-sm font-semibold mb-1">Paystack</div>
                        <div className="text-xs text-gray-400">Local Payment (GHS)</div>
                      </button>
                      <button
                        onClick={() => setPaymentMethod('crypto')}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          paymentMethod === 'crypto'
                            ? 'border-emerald-500 bg-emerald-500/10'
                            : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                        }`}
                      >
                        <div className="text-sm font-semibold mb-1">Cryptocurrency</div>
                        <div className="text-xs text-gray-400">BTC / ETH / USDT</div>
                      </button>
                    </div>
                  </div>

                  {paymentMethod === 'crypto' ? (
                    /* Crypto Payment Interface */
                    <div className="space-y-6">
                      {/* Crypto Type Selector */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-3">
                          Select Cryptocurrency
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            onClick={() => setSelectedCrypto('bitcoin')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              selectedCrypto === 'bitcoin'
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                            }`}
                          >
                            <div className="text-xs font-semibold">Bitcoin</div>
                            <div className="text-xs text-gray-400">BTC</div>
                          </button>
                          <button
                            onClick={() => setSelectedCrypto('ethereum')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              selectedCrypto === 'ethereum'
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                            }`}
                          >
                            <div className="text-xs font-semibold">Ethereum</div>
                            <div className="text-xs text-gray-400">ETH</div>
                          </button>
                          <button
                            onClick={() => setSelectedCrypto('usdt')}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              selectedCrypto === 'usdt'
                                ? 'border-emerald-500 bg-emerald-500/10'
                                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                            }`}
                          >
                            <div className="text-xs font-semibold">Tether</div>
                            <div className="text-xs text-gray-400">USDT</div>
                          </button>
                        </div>
                      </div>

                      {/* Payment Address */}
                      {cryptoAddresses && cryptoAddresses[selectedCrypto] && (
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <div className="text-sm font-medium text-gray-300 mb-2">
                            Send payment to this address:
                          </div>
                          <div className="bg-gray-900 rounded p-3 mb-3 break-all text-sm font-mono text-emerald-400">
                            {cryptoAddresses[selectedCrypto].address}
                          </div>

                          {/* Network Information - Bold and Prominent */}
                          {cryptoAddresses[selectedCrypto].network && (
                            <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3 mb-3">
                              <div className="flex items-center gap-2">
                                <span className="text-yellow-400 font-bold text-xs">⚠️ IMPORTANT:</span>
                                <span className="text-yellow-300 font-bold text-sm">
                                  Use {cryptoAddresses[selectedCrypto].network} Network Only
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="text-xs text-gray-400">
                            {cryptoAddresses[selectedCrypto].description}
                          </div>
                        </div>
                      )}

                      {/* Instructions */}
                      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-blue-400 mb-2 text-sm">Payment Instructions</h4>
                        <ol className="text-xs text-gray-300 space-y-1 list-decimal list-inside">
                          <li>
                            <strong className="text-yellow-400">NETWORK:</strong> {cryptoAddresses && cryptoAddresses[selectedCrypto] && cryptoAddresses[selectedCrypto].network ? (
                              <span className="font-bold text-yellow-300">{cryptoAddresses[selectedCrypto].network}</span>
                            ) : 'Check network above'}
                          </li>
                          <li>Send {formatPrice(parseFloat(product.price), product.currency)} equivalent in {selectedCrypto.toUpperCase()} to the address above</li>
                          <li>Copy your transaction ID/hash after payment</li>
                          <li>Paste the transaction ID below and submit</li>
                          <li>Your order will be reviewed and approved within 24 hours</li>
                          <li className="text-red-400 font-semibold">⚠️ Wrong network = Lost funds! Double-check the network before sending.</li>
                        </ol>
                      </div>

                      {/* Transaction ID Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Transaction ID / Hash <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={cryptoTxId}
                          onChange={(e) => setCryptoTxId(e.target.value)}
                          placeholder="Enter your transaction ID/hash"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500"
                        />
                        <p className="text-xs text-gray-400 mt-2">
                          This is the unique reference from your crypto wallet after sending payment
                        </p>
                      </div>

                      {/* Submit Button */}
                      <button
                        onClick={handleCryptoPayment}
                        disabled={submittingCrypto || !cryptoTxId.trim()}
                        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold text-lg transition-colors"
                      >
                        {submittingCrypto ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Submit Crypto Payment
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-500 text-center">
                        Your payment will be manually reviewed and approved within 24 hours
                      </p>
                    </div>
                  ) : (
                    /* Paystack Payment Button */
                    <>
                      <button
                        onClick={handlePurchase}
                        disabled={purchasing}
                        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold text-lg transition-colors"
                      >
                        {purchasing ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Processing...
                          </>
                        ) : (
                          <>
                            <ShoppingCart size={20} />
                            Buy Now - {formatPrice(parseFloat(product.price), product.currency)}
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-500 text-center mt-4">
                        Secure payment powered by Paystack
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Demo Link */}
            {product.demo_url && (
              <a
                href={product.demo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors mb-6"
              >
                <ExternalLink size={20} />
                View Live Demo
              </a>
            )}

            {/* What You Get */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">What's Included</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Code size={20} className="text-emerald-400 mt-0.5" />
                  <span className="text-gray-300">Complete source code</span>
                </li>
                <li className="flex items-start gap-3">
                  <Download size={20} className="text-emerald-400 mt-0.5" />
                  <span className="text-gray-300">Lifetime access & updates</span>
                </li>
                <li className="flex items-start gap-3">
                  <Shield size={20} className="text-emerald-400 mt-0.5" />
                  <span className="text-gray-300">Commercial license</span>
                </li>
                <li className="flex items-start gap-3">
                  <RefreshCcw size={20} className="text-emerald-400 mt-0.5" />
                  <span className="text-gray-300">Free updates</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Full Description */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-16"
        >
          <h2 className="text-3xl font-bold mb-6">About This Product</h2>
          <div className="prose prose-lg prose-invert max-w-none bg-gray-800 rounded-lg p-8">
            <p className="text-gray-300 whitespace-pre-line">{product.description}</p>
          </div>
        </motion.div>

        {/* Tech Stack */}
        {product.tech_stack && product.tech_stack.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-12"
          >
            <h2 className="text-3xl font-bold mb-6">Technology Stack</h2>
            <div className="flex flex-wrap gap-4">
              {product.tech_stack.map((tech) => (
                <div
                  key={tech}
                  className="px-6 py-3 bg-gray-800 rounded-lg border border-gray-700 hover:border-emerald-500 transition-colors"
                >
                  <span className="text-gray-300 font-medium">{tech}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Related Products */}
        {product.related_products && product.related_products.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-16"
          >
            <h2 className="text-3xl font-bold mb-8">You May Also Like</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {product.related_products.map((related) => (
                <Link
                  key={related.id}
                  to={`/shop/${related.slug}`}
                  className="bg-gray-800 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all duration-300 group"
                >
                  {related.image_url && (
                    <div className="h-40 overflow-hidden">
                      <img
                        src={related.image_url}
                        alt={related.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold mb-2 group-hover:text-emerald-400 transition-colors">
                      {related.name}
                    </h3>
                    <p className="text-sm text-gray-400 line-clamp-2 mb-3">
                      {related.short_description}
                    </p>
                    <div className="text-lg font-bold text-emerald-400">
                      {formatPrice(parseFloat(related.price), related.currency)}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
